import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { ENTER, SPACE } from '@angular/cdk/keycodes';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Overlay, OverlayContainer, ViewportRuler, ScrollDispatcher, GlobalPositionStrategy, FlexibleConnectedPositionStrategy, OverlayRef, BlockScrollStrategy, CloseScrollStrategy } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { TemplatePortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import { Directive, ElementRef, EventEmitter, HostBinding, HostListener, Inject, Input, NgZone, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { dropdownModalBreakpoint } from '@app/core/constants/style-constants';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MenuCloseReason, MenuComponent } from '../menu/menu.component';


/**
 * Trigger of a menu.
 * 
 * Documentation see `MenuComponent`.
 */
@Directive({
  selector: '[appMenuTrigger]'
})
export class MenuTriggerDirective implements OnInit, OnDestroy {
  /** Emits true if menu was opened, false if menu was closed */
  private onMenuToggleSubject = new Subject<boolean>();

  private overlayRef: OverlayRef;
  private menuPortal: TemplatePortal;

  private isMobileView = false;

  private openedBy: Exclude<FocusOrigin, 'program' | null> | undefined = undefined;


  public get menuOpened(): boolean {
    return this._menuOpened;
  }
  private _menuOpened = false;

  @HostBinding('attr.aria-expanded')
  public get ariaIsExpanded() {
    return this.menuOpened || null;
  }
  
  @HostBinding('attr.aria-controls')
  public get ariaControls() {
    return this.menuOpened ? this.menu.menuId : null;
  }

  @HostBinding('attr.aria-haspopup')
  public ariaHasPopup = true;

  // tslint:disable-next-line: no-input-rename
  @Input('menuTriggerFor')
  public menu: MenuComponent;

  /** Emits true if menu is opened and false if menu is closed */
  @Output()
  public menuToggle = new EventEmitter<boolean>();

  constructor(
    private element: ElementRef<HTMLElement>,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private viewportRuler: ViewportRuler,
    @Inject(DOCUMENT) private document: Document,
    private platform: Platform,
    private overlayContainer: OverlayContainer,
    private breakpointObserver: BreakpointObserver,
    private scrollDispatcher: ScrollDispatcher,
    private ngZone: NgZone,
    private focusMonitor: FocusMonitor
  ) { }

  public ngOnInit() {
    this.onMenuToggleSubject.subscribe((open) => {
      this.menuToggle.emit(open);
    });

    this.menu.closed.subscribe((reason) => {
      this.closeMenu(reason);
    });
  }

  public ngOnDestroy() {
    // Force-close menu if it is still opened
    this.overlayRef?.detach();

    this.onMenuToggleSubject.complete();
  }

  public openMenu() {
    if (this.menuOpened) {
      return;
    }

    this.overlayRef = this.overlay.create({
      panelClass: 'scg-overlay-pane',
      hasBackdrop: true,
      disposeOnNavigation: true
    });

    this.menuPortal = new TemplatePortal(this.menu.menuPortalRef, this.viewContainerRef);
    this.overlayRef.attach(this.menuPortal);

    this.overlayRef.backdropClick().subscribe(() => {
      this.closeMenu('click');
    });

    this.overlayRef.detachments().subscribe(() => {
      // After dispose the hide method may not be called. Therefore we need to cleanup here
      this._menuOpened = false;
      this.onMenuToggleSubject.next(false);
    });

    // Update menu content width if the trigger size changes
    this.viewportRuler.change()
      .pipe(
        takeUntil(this.onMenuToggleSubject.pipe(filter(open => !open))),
        filter(() => !this.isMobileView)
      )
      .subscribe(() => {
        this.menu.minWidth = this.element.nativeElement.offsetWidth;
        this.overlayRef.updateSize({ minWidth: this.element.nativeElement.offsetWidth });
      });

    // Intial and dynamic update of positionStrategy and backdrop style
    this.breakpointObserver.observe([
      `(orientation: portrait) and (max-width: ${dropdownModalBreakpoint})`,
      `(orientation: landscape) and (max-height: ${dropdownModalBreakpoint})`
    ])
      .pipe(takeUntil(this.onMenuToggleSubject.pipe(filter(open => !open))))
      .subscribe((state) => {
        if (state.matches) {
          this.isMobileView = true;

          this.overlayRef.updatePositionStrategy(this.getGlobalPositionStrategy());
          this.menu.minWidth = null;
          this.overlayRef.updateSize({ minWidth: null });
          this.overlayRef.updateScrollStrategy(new BlockScrollStrategy(this.viewportRuler, this.document));
          this.overlayRef.backdropElement.classList.remove('cdk-overlay-transparent-backdrop');
        } else {
          this.isMobileView = false;

          this.overlayRef.updatePositionStrategy(this.getFlexibleConnectedPositionStrategy());
          this.menu.minWidth = this.element.nativeElement.offsetWidth;
          this.overlayRef.updateSize({ minWidth: this.element.nativeElement.offsetWidth });
          this.overlayRef.updateScrollStrategy(new CloseScrollStrategy(this.scrollDispatcher, this.ngZone, this.viewportRuler));
          this.overlayRef.backdropElement.classList.add('cdk-overlay-transparent-backdrop');
        }
      });

    this.menu.focusFirstItem(this.openedBy ?? 'program');

    this._menuOpened = true;
    this.onMenuToggleSubject.next(true);
  }

  /**
   * Closes menu
   * @param keepFocus If true, the menu trigger is focused on close (default behavior)
   */
   public closeMenu(reason: MenuCloseReason, keepFocus = true) {
    this.overlayRef.detach();

    // Check if an item is focused because another element could have already "stolen" the focus (e.g. a modal)
    // and we don't want to steal it back
    const itemFocused = this.menu.items.some(item => item.focused);

    if (keepFocus && itemFocused && (reason === 'keydown' || this.openedBy != null)) {
      if (this.openedBy != null) {
        this.focusMonitor.focusVia(this.element, this.openedBy);
      } else {
        this.element.nativeElement.focus();
      }
    }

    this.openedBy = undefined;

    this._menuOpened = false;
    this.onMenuToggleSubject.next(false);
  }

  @HostListener('click', ['$event'])
  public onClick(event: MouseEvent) {
    this.openedBy = 'mouse';
    this.openMenu();
  }

  @HostListener('keydown', ['$event'])
  public onKeydown(event: KeyboardEvent) {
    if (event.keyCode === ENTER || event.keyCode === SPACE) {
      this.openedBy = 'keyboard';
      this.openMenu();
    }
  }

  private getGlobalPositionStrategy(): GlobalPositionStrategy {
    const strategy = new GlobalPositionStrategy();
    strategy.centerHorizontally();
    strategy.centerVertically();
    return strategy;
  }

  private getFlexibleConnectedPositionStrategy(): FlexibleConnectedPositionStrategy {
    const strategy = new FlexibleConnectedPositionStrategy(this.element,
      this.viewportRuler,
      this.document,
      this.platform,
      this.overlayContainer
    );

    // The next two lines make sure, we set a max-height and show a scrollbar
    // instead of repositioning somewhere else on screen or clipping out of viewport
    strategy.withFlexibleDimensions(true);
    strategy.withPush(false);

    strategy.withViewportMargin(10);
    strategy.withPositions([
      {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top'},
      {originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top'},
      {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom'},
      {originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom'}
    ]);
    return strategy;
  }

}
